// frontend-dash/src/pages/EditableEventPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { trackAdminEventCreated, trackAdminEventUpdated } from '../utils/analytics';
import EditableField from '../components/EditableField';
import {
    CalendarIcon as Calendar, LocationIcon as MapPin, TimeIcon as Clock,
    ArrowLeftIcon as ArrowLeft, MailIcon as Mail, VerifiedIcon as ShieldCheck,
    UserIcon as User, DollarIcon as DollarSign, GiftIcon as Gift,
    StarIcon as Star, HeartIcon as Heart, AlertCircleIcon as AlertCircle,
    InfoIcon as Info, CheckIcon as Check,
    EditIcon as Edit2, SaveIcon as Save, PlusIcon as Plus,
    DeleteIcon as Trash, CloseIcon as X,
    SettingsIcon as Settings, GroupIcon as Users, FileEditIcon, DownloadIcon
} from '../components/Icons';
import { downloadIcsFile } from '../utils/calendarExport';
import EventCard from '../components/EventCard';

const MAIN_URL = process.env.REACT_APP_MAIN_URL || (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname.replace(/^dash\./, '')}` : '');

const ICON_OPTIONS = [
    { value: 'Calendar', label: 'Kalender' },
    { value: 'Clock', label: 'Uhrzeit' },
    { value: 'MapPin', label: 'Ort' },
    { value: 'Check', label: 'Check' },
    { value: 'Info', label: 'Info' },
    { value: 'Mail', label: 'Mail' },
    { value: 'User', label: 'Person' },
    { value: 'ShieldCheck', label: 'Sicherheit' },
    { value: 'DollarSign', label: 'Kosten' },
    { value: 'Gift', label: 'Geschenk' },
    { value: 'Star', label: 'Stern' },
    { value: 'Heart', label: 'Herz' },
    { value: 'AlertCircle', label: 'Warnung' },
];

const IMAGE_SIZES = [
    { value: 'small', label: 'Klein' },
    { value: 'medium', label: 'Mittel' },
    { value: 'large', label: 'Groß' },
    { value: 'full', label: 'Volle Breite' },
];

const SectionEditor = ({ section, index, onUpdate, onDelete, onMove, totalSections, iconMap, onImageUpload }) => {
    const [editing, setEditing] = useState(false);
    const [data, setData] = useState(section);

    const handleSave = () => {
        onUpdate(index, data);
        setEditing(false);
    };

    // --- Item helpers for list/grid ---
    const updateItem = (i, value) => {
        const items = [...(data.items || [])];
        items[i] = value;
        setData({ ...data, items });
    };
    const removeItem = (i) => {
        setData({ ...data, items: (data.items || []).filter((_, idx) => idx !== i) });
    };
    const addItem = () => {
        setData({ ...data, items: [...(data.items || []), ''] });
    };

    // --- Icon-item helpers ---
    const updateIconItem = (i, field, value) => {
        const items = [...(data.iconItems || [])];
        items[i] = { ...items[i], [field]: value };
        setData({ ...data, iconItems: items });
    };
    const removeIconItem = (i) => {
        setData({ ...data, iconItems: (data.iconItems || []).filter((_, idx) => idx !== i) });
    };
    const addIconItem = () => {
        setData({ ...data, iconItems: [...(data.iconItems || []), { icon: 'Check', text: '' }] });
    };

    // --- Collapsed preview ---
    if (!editing) {
        const iconMapLocal = iconMap || {};
        // Show a mini-preview of the section content
        const previewContent = () => {
            switch (section.type) {
                case 'icon-list':
                    return (
                        <ul className="space-y-1.5 mt-2">
                            {(section.iconItems || []).map((item, i) => {
                                const Icon = iconMapLocal[item.icon];
                                return (
                                    <li key={i} className="flex items-center gap-2 text-body-small text-surface-onVariant">
                                        {Icon ? <Icon sx={{ fontSize: 14 }} className="text-primary" /> : <span className="w-3.5 h-3.5 rounded-full bg-primary/30" />}
                                        <span>{item.text || '—'}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    );
                case 'list':
                    return (
                        <ul className="space-y-1 mt-2">
                            {(section.items || []).map((item, i) => (
                                <li key={i} className="flex items-center gap-2 text-body-small text-surface-onVariant">
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                    <span>{item || '—'}</span>
                                </li>
                            ))}
                        </ul>
                    );
                case 'grid':
                    return (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {(section.items || []).map((item, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-md bg-surface-container text-body-small text-surface-onVariant">{item || '—'}</span>
                            ))}
                        </div>
                    );
                case 'image':
                    return section.imageUrl ? (
                        <img src={section.imageUrl} alt="" className="mt-2 h-16 rounded-lg object-cover" />
                    ) : <p className="text-body-small text-surface-onVariant mt-1">Kein Bild</p>;
                default:
                    return <p className="text-body-small text-surface-onVariant line-clamp-2 mt-1">{section.content || '—'}</p>;
            }
        };

        return (
            <div className="section-editor-item rounded-2xl p-4 bg-surface-containerLow relative group cursor-pointer border border-transparent hover:border-primary/20 transition-all"
                 onClick={() => setEditing(true)}>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-label-small font-bold text-primary uppercase tracking-wider">
                        {section.type === 'icon-list' ? 'Icon-Liste' : section.type === 'info-box' ? 'Info-Box' : section.type} — {section.title || 'Ohne Titel'}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onMove && index > 0 && (
                            <button onClick={(e) => { e.stopPropagation(); onMove(index, 'up'); }}
                                className="p-1.5 rounded-lg hover:bg-primary/10 text-primary text-xs">▲</button>
                        )}
                        {onMove && index < totalSections - 1 && (
                            <button onClick={(e) => { e.stopPropagation(); onMove(index, 'down'); }}
                                className="p-1.5 rounded-lg hover:bg-primary/10 text-primary text-xs">▼</button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                            className="p-1.5 rounded-lg hover:bg-primary/10 text-primary"><Edit2 sx={{ fontSize: 16 }} /></button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(index); }}
                            className="p-1.5 rounded-lg hover:bg-error/10 text-error"><Trash sx={{ fontSize: 16 }} /></button>
                    </div>
                </div>
                {previewContent()}
            </div>
        );
    }

    // --- Expanded edit form ---
    return (
        <div className="rounded-2xl p-5 bg-primary/5 border-2 border-primary/30 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
                <span className="text-label-small font-bold text-primary uppercase">Section bearbeiten</span>
                <div className="flex gap-1.5">
                    <button onClick={() => { setData(section); setEditing(false); }} className="p-1.5 rounded-lg hover:bg-surface-containerHigh text-surface-onVariant"><X sx={{ fontSize: 18 }} /></button>
                    <button onClick={handleSave} className="px-3 py-1.5 rounded-lg bg-primary text-primary-on font-bold text-label-small flex items-center gap-1.5">
                        <Check sx={{ fontSize: 16 }} /> Speichern
                    </button>
                </div>
            </div>
            <div className="space-y-3">
                <select value={data.type} onChange={e => setData({...data, type: e.target.value})}
                    className="md3-textfield-outlined h-12 text-body-medium">
                    <option value="text">Text</option>
                    <option value="info-box">Info-Box</option>
                    <option value="list">Liste</option>
                    <option value="grid">Grid</option>
                    <option value="icon-list">Icon-Liste</option>
                    <option value="image">Bild</option>
                </select>
                <input value={data.title || ''} onChange={e => setData({...data, title: e.target.value})}
                    className="md3-textfield-outlined" placeholder="Titel der Section" />

                {/* TEXT / INFO-BOX */}
                {(data.type === 'text' || data.type === 'info-box') && (
                    <textarea value={data.content || ''} onChange={e => setData({...data, content: e.target.value})}
                        className="md3-textfield-outlined min-h-[120px] py-3" placeholder="Inhalt..." />
                )}

                {/* LIST / GRID — individual items */}
                {(data.type === 'list' || data.type === 'grid') && (
                    <div className="space-y-2">
                        <label className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider">Einträge</label>
                        {(data.items || []).map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-xs text-primary font-bold">{i + 1}</div>
                                <input value={item} onChange={e => updateItem(i, e.target.value)}
                                    className="md3-textfield-outlined flex-1" placeholder={`Eintrag ${i + 1}...`} />
                                <button onClick={() => removeItem(i)}
                                    className="p-1.5 rounded-lg hover:bg-error/10 text-error shrink-0"><Trash sx={{ fontSize: 16 }} /></button>
                            </div>
                        ))}
                        <button onClick={addItem}
                            className="md3-btn-tonal w-full justify-center gap-1.5 text-label-small mt-1">
                            <Plus sx={{ fontSize: 16 }} /> Eintrag hinzufügen
                        </button>
                    </div>
                )}

                {/* ICON-LIST — icon selector + text per item */}
                {data.type === 'icon-list' && (
                    <div className="space-y-2">
                        <label className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider">Icon-Einträge</label>
                        {(data.iconItems || []).map((item, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded-xl bg-surface-container">
                                <select value={item.icon || 'Check'} onChange={e => updateIconItem(i, 'icon', e.target.value)}
                                    className="bg-surface-containerLowest border border-outlineVariant rounded-lg px-2 py-1.5 text-body-small w-36 shrink-0">
                                    {ICON_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <input value={item.text || ''} onChange={e => updateIconItem(i, 'text', e.target.value)}
                                    className="md3-textfield-outlined flex-1" placeholder="Text..." />
                                <button onClick={() => removeIconItem(i)}
                                    className="p-1.5 rounded-lg hover:bg-error/10 text-error shrink-0"><Trash sx={{ fontSize: 16 }} /></button>
                            </div>
                        ))}
                        <button onClick={addIconItem}
                            className="md3-btn-tonal w-full justify-center gap-1.5 text-label-small mt-1">
                            <Plus sx={{ fontSize: 16 }} /> Icon-Eintrag hinzufügen
                        </button>
                    </div>
                )}

                {/* IMAGE */}
                {data.type === 'image' && (
                    <div className="space-y-3">
                        <div className="flex gap-2">
                            <input value={data.imageUrl || ''} onChange={e => setData({...data, imageUrl: e.target.value})}
                                className="md3-textfield-outlined flex-grow" placeholder="Bild-URL" />
                            {onImageUpload && (
                                <label className="md3-btn-outlined text-label-small flex items-center justify-center shrink-0 cursor-pointer h-12 px-3">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            try {
                                                const url = await onImageUpload(file);
                                                setData(prev => ({ ...prev, imageUrl: url }));
                                            } catch (err) {
                                                console.error('File upload failed:', err);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                    <span>Hochladen</span>
                                </label>
                            )}
                        </div>
                        <input value={data.imageCaption || ''} onChange={e => setData({...data, imageCaption: e.target.value})}
                            className="md3-textfield-outlined" placeholder="Bildunterschrift (optional)" />
                        <select value={data.imageSize || 'medium'} onChange={e => setData({...data, imageSize: e.target.value})}
                            className="md3-textfield-outlined h-12 text-body-medium">
                            {IMAGE_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        {data.imageUrl && <img src={data.imageUrl} alt="" className="rounded-xl max-h-40 object-cover border border-outlineVariant" />}
                    </div>
                )}
            </div>
        </div>
    );
};

const EditableEventPage = () => {
    const { slug, id } = useParams();
    const { user, API_URL } = useContext(AuthContext);
    const navigate = useNavigate();
    
    const handleImageUpload = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${user.token}`,
            },
        };
        const res = await axios.post(`${API_URL}/files/upload`, formData, config);
        return res.data.url;
    };
    const isNew = id === 'new';
    const [event, setEvent] = useState(isNew ? {
        title: '', description: '', date: new Date().toISOString().split('T')[0],
        startTime: '', endTime: '', location: '', category: 'Sonstiges',
        status: 'draft', isRegistrationEnabled: false, pageContent: { hero: { title: '', subtitle: '', imageUrl: '' }, sections: [] }
    } : null);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [saveMsg, setSaveMsg] = useState('');
    const [activePanel, setActivePanel] = useState(null); // null, 'settings', 'email'

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/groups`);
                setGroups(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Failed to load groups in EditableEventPage', err);
            }
        };
        fetchGroups();

        if (isNew) return;
        const fetchEvent = async () => {
            try {
                const config = { headers: { Authorization: `Bearer ${user.token}` } };
                if (id) {
                    // Load by MongoDB _id (from dashboard route)
                    const { data } = await axios.get(`${API_URL}/events/${id}`, config);
                    setEvent(data);
                } else if (slug) {
                    // Load by slug (from inline edit route)
                    const response = await axios.get(`${API_URL}/events`, config);
                    const found = response.data.find(e => e.slug === slug);
                    if (!found) setError('Event nicht gefunden');
                    else setEvent(found);
                }
            } catch (err) {
                setError('Fehler beim Laden des Events');
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [slug, id, isNew, API_URL, user]);

    const saveField = async (field, value) => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const updateData = {};

            if (field.startsWith('pageContent.hero.')) {
                const heroField = field.replace('pageContent.hero.', '');
                updateData.pageContent = {
                    ...event.pageContent,
                    hero: { ...event.pageContent?.hero, [heroField]: value }
                };
            } else if (field === 'pageContent.sections') {
                updateData.pageContent = { ...event.pageContent, sections: value };
            } else {
                updateData[field] = value;
            }

            if (isNew && !event._id) {
                // Update local state only for new events
                setEvent(prev => {
                    if (field.startsWith('pageContent.')) {
                        return { ...prev, pageContent: updateData.pageContent };
                    }
                    return { ...prev, [field]: value };
                });
                return;
            } else {
                await axios.put(`${API_URL}/events/${event._id}`, updateData, config);
                trackAdminEventUpdated(event.title);
                setEvent(prev => {
                    if (field.startsWith('pageContent.')) {
                        return { ...prev, pageContent: updateData.pageContent };
                    }
                    return { ...prev, [field]: value };
                });
                setSaveMsg('Gespeichert!');
            }
            setTimeout(() => setSaveMsg(''), 2000);
        } catch (err) {
            console.error('Save failed:', err);
            throw err;
        }
    };

    // For new events: save all data at once
    const handleCreateEvent = async () => {
        if (!event.title || !event.date) {
            alert('Titel und Datum sind Pflichtfelder.');
            return;
        }
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.post(`${API_URL}/events`, event, config);
            setEvent(data);
            trackAdminEventCreated(event.title);
            setSaveMsg('Event erstellt!');
            setTimeout(() => setSaveMsg(''), 2000);
            navigate(`/events/edit/${data._id}`, { replace: true });
        } catch (err) {
            console.error('Create failed:', err);
            alert('Fehler beim Erstellen: ' + (err.response?.data?.message || err.message));
        }
    };

    const handlePublish = async () => {
        try {
            await saveField('status', 'published');
            setSaveMsg('Event veröffentlicht!');
        } catch (err) {
            console.error('Publish failed:', err);
            alert('Fehler beim Veröffentlichen');
        }
    };

    const handleSectionUpdate = (index, updatedSection) => {
        const newSections = [...(event.pageContent?.sections || [])];
        newSections[index] = updatedSection;
        saveField('pageContent.sections', newSections);
    };

    const handleSectionDelete = (index) => {
        if (!window.confirm('Section wirklich löschen?')) return;
        const newSections = (event.pageContent?.sections || []).filter((_, i) => i !== index);
        saveField('pageContent.sections', newSections);
    };

    const handleAddSection = (type = 'text') => {
        const templates = {
            'text': { type: 'text', title: 'Neue Section', content: '' },
            'info-box': { type: 'info-box', title: 'Neue Info-Box', content: '' },
            'list': { type: 'list', title: 'Neue Liste', items: [''] },
            'grid': { type: 'grid', title: 'Neues Grid', items: [''] },
            'icon-list': { type: 'icon-list', title: 'Neue Icon-Liste', iconItems: [{ icon: 'Check', text: '' }] },
            'image': { type: 'image', title: '', imageUrl: '', imageCaption: '', imageSize: 'medium' },
        };
        const newSections = [...(event.pageContent?.sections || []), templates[type] || templates['text']];
        saveField('pageContent.sections', newSections);
    };

    const handleSectionMove = (index, direction) => {
        const sections = [...(event.pageContent?.sections || [])];
        const target = index + (direction === 'up' ? -1 : 1);
        if (target < 0 || target >= sections.length) return;
        [sections[index], sections[target]] = [sections[target], sections[index]];
        saveField('pageContent.sections', sections);
    };

    const iconMap = { Calendar, MapPin, Clock, Mail, ShieldCheck, User, DollarSign, Gift, Star, Heart, AlertCircle, Info, Check };

    const renderSection = (section, index) => {
        const base = "md3-card-elevated bg-surface-containerLow p-6 mb-6 text-surface-on";
        switch (section.type) {
            case 'text': return (
                <div key={index} className={base}>
                    {section.title && <h3 className="text-headline-small font-bold mb-4 text-primary">{section.title}</h3>}
                    <p className="whitespace-pre-wrap leading-relaxed text-body-large text-surface-onVariant">{section.content}</p>
                </div>);
            case 'info-box': return (
                <div key={index} className={`${base} border-l-4 border-primary bg-primary-container/10`}>
                    {section.title && <h3 className="text-headline-small font-bold mb-4 text-primary">{section.title}</h3>}
                    <p className="whitespace-pre-wrap leading-relaxed text-body-large text-surface-onVariant">{section.content}</p>
                </div>);
            case 'list': return (
                <div key={index} className={base}>
                    {section.title && <h3 className="text-headline-small font-bold mb-4 text-primary">{section.title}</h3>}
                    <ul className="space-y-3">{section.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-surface-container">
                            <div className="mt-1 min-w-[6px] h-[6px] rounded-full bg-primary" />
                            <span className="text-body-large text-surface-onVariant">{item}</span>
                        </li>))}</ul>
                </div>);
            case 'icon-list': return (
                <div key={index} className={base}>
                    {section.title && <h3 className="text-headline-small font-bold mb-4 text-primary">{section.title}</h3>}
                    <ul className="space-y-3">{section.iconItems?.map((item, i) => {
                        const IconComponent = iconMap[item.icon];
                        return (
                            <li key={i} className="p-4 rounded-xl bg-surface-container hover:bg-surface-containerHigh transition-colors flex items-center gap-4">
                                <div className="p-2 rounded-full bg-primary-container text-primary">
                                    {IconComponent && <IconComponent sx={{ fontSize: 20 }} />}
                                </div>
                                <span className="text-body-large text-surface-onVariant">{item.text}</span>
                            </li>
                        );
                    })}</ul>
                </div>);
            case 'grid': return (
                <div key={index} className={base}>
                    {section.title && <h3 className="text-headline-small font-bold mb-4 text-primary">{section.title}</h3>}
                    <div className={`grid gap-4 md:grid-cols-${section.gridColumns || 2}`}>
                        {section.items?.map((item, i) => (
                            <div key={i} className="p-4 rounded-xl bg-surface-container hover:bg-surface-containerHigh transition-colors">
                                <span className="text-body-large text-surface-onVariant">{item}</span>
                            </div>
                        ))}
                    </div>
                </div>);
            case 'image': return (
                <div key={index} className={base}>
                    {section.title && <h3 className="text-headline-small font-bold mb-4 text-primary">{section.title}</h3>}
                    {section.imageUrl && <img src={section.imageUrl} alt={section.title || ''} className="rounded-2xl shadow-elevation-2 max-w-md mx-auto" />}
                    {section.imageCaption && <p className="mt-3 text-label-large text-surface-onVariant italic text-center">{section.imageCaption}</p>}
                </div>);
            default: return null;
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-surface flex items-center justify-center">
            <div className="md3-progress-circular border-4 border-primary border-t-transparent" />
            <span className="ml-4 text-surface-onVariant">Lade Event...</span>
        </div>);

    if (error || !event) return (
        <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6">
            <div className="bg-error-container text-error-onContainer p-8 rounded-3xl max-w-md text-center">
                <AlertCircle size={48} className="mx-auto mb-4" />
                <h2 className="text-headline-small font-bold mb-2">Fehler</h2>
                <p className="text-body-large mb-6">{error || 'Event nicht gefunden'}</p>
                <button onClick={() => navigate('/')} className="md3-btn-filled">Zurück</button>
            </div>
        </div>);

    const { pageContent } = event;

    return (
        <div className="min-h-screen bg-surface pb-20">
            {/* Edit Mode Banner */}
            <div className="edit-mode-banner px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/events')} className="w-8 h-8 rounded-full hover:bg-surface-on/5 flex items-center justify-center transition-colors">
                        <ArrowLeft sx={{ fontSize: 20 }} />
                    </button>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Edit2 sx={{ fontSize: 18 }} className="text-primary" />
                    </div>
                    <div>
                        <span className="text-label-large font-bold text-primary">{isNew && !event._id ? 'Neues Event' : 'Bearbeitungsmodus'}</span>
                        <span className="text-label-small text-surface-onVariant ml-2 hidden sm:inline">{event.title || 'Klicke auf Felder um sie zu bearbeiten'}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {saveMsg && <span className="text-label-medium text-primary font-bold animate-fade-in">✓ {saveMsg}</span>}
                    {event._id && event.status === 'draft' && (
                        <button
                            onClick={handlePublish}
                            className="md3-btn-filled gap-1.5 text-label-medium !bg-primary !text-primary-on shadow-elevation-1 animate-pulse"
                        >
                            <Check sx={{ fontSize: 16 }} /> Veröffentlichen
                        </button>
                    )}
                    {event._id && event.status === 'published' && (
                        <span className="px-3 py-1 bg-green-500/10 text-green-700 dark:text-green-400 rounded-full text-xs font-bold border border-green-500/20">
                            ✓ Veröffentlicht
                        </span>
                    )}
                    <button onClick={() => setActivePanel(activePanel === 'settings' ? null : 'settings')}
                        className={`md3-btn-text text-label-medium gap-1 ${activePanel === 'settings' ? 'text-primary' : ''}`}>
                        <Settings sx={{ fontSize: 16 }} /> Einstellungen
                    </button>
                    <button onClick={() => setActivePanel(activePanel === 'email' ? null : 'email')}
                        className={`md3-btn-text text-label-medium gap-1 ${activePanel === 'email' ? 'text-primary' : ''}`}>
                        <Mail sx={{ fontSize: 16 }} /> E-Mail
                    </button>
                    {event._id && (
                        <>
                            <button
                                onClick={() => navigate(`/blog/new?eventId=${event._id}&title=${encodeURIComponent(`Rückblick: ${event.title}`)}`)}
                                className="md3-btn-tonal text-label-medium gap-1 !bg-secondary-container !text-secondary-onContainer"
                                title="Rückblick / Blogbeitrag zu diesem Event verfassen"
                            >
                                <FileEditIcon sx={{ fontSize: 16 }} /> Rückblick
                            </button>
                            <button
                                onClick={() => downloadIcsFile(event)}
                                className="md3-btn-text text-label-medium gap-1"
                                title="iCal Kalenderdatei (.ics) herunterladen"
                            >
                                <DownloadIcon sx={{ fontSize: 16 }} /> .ics
                            </button>
                            <Link to={`/events/${event._id}/registrations`}
                                className="md3-btn-text text-label-medium gap-1">
                                <Users sx={{ fontSize: 16 }} /> Anmeldungen
                            </Link>
                        </>
                    )}
                    {isNew && !event._id && (
                        <button onClick={handleCreateEvent} className="md3-btn-filled gap-1.5 text-label-medium">
                            <Save sx={{ fontSize: 16 }} /> Event erstellen
                        </button>
                    )}
                    {(slug || event.slug) && (
                        <a href={`${MAIN_URL}/events/${slug || event.slug}`} target="_blank" rel="noopener noreferrer"
                            className="md3-btn-outlined text-label-medium">
                            Zur Ansicht
                        </a>
                    )}
                </div>
            </div>

            {/* Settings Panel */}
            {activePanel === 'settings' && (
                <div className="border-b border-outlineVariant bg-surface-containerLow animate-fade-in">
                    <div className="container mx-auto px-4 py-6 max-w-4xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-title-medium font-bold flex items-center gap-2">
                                <Settings sx={{ fontSize: 20 }} className="text-primary" /> Einstellungen
                            </h3>
                            <button onClick={() => setActivePanel(null)} className="md3-icon-btn"><X sx={{ fontSize: 18 }} /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-label-small font-bold text-surface-onVariant">Kategorie</label>
                                <EditableField value={event.category || 'Sonstiges'} onSave={v => saveField('category', v)} type="select" label="Kategorie"
                                    selectOptions={[{value:'Gruppenstunde',label:'Gruppenstunde'},{value:'Lager',label:'Lager'},{value:'Aktion',label:'Aktion'},{value:'Sonstiges',label:'Sonstiges'}]}>
                                    <div className="p-3 rounded-lg bg-surface-container"><span className="text-body-medium font-bold">{event.category || 'Sonstiges'}</span></div>
                                </EditableField>
                            </div>
                            <div className="space-y-2">
                                <label className="text-label-small font-bold text-surface-onVariant">Verknüpfte Gruppe / Stufe</label>
                                <EditableField
                                    value={event.linkedGroup?._id || event.linkedGroup?.slug || event.linkedGroup || ''}
                                    onSave={v => saveField('linkedGroup', v || null)}
                                    type="select"
                                    label="Verknüpfte Gruppe"
                                    selectOptions={[
                                        { value: '', label: 'Keine Gruppe' },
                                        ...groups.map(g => ({ value: g.slug || g._id, label: `${g.name} (${g.altersstufe || 'Gruppe'})` }))
                                    ]}
                                >
                                    <div className="p-3 rounded-lg bg-surface-container">
                                        <span className="text-body-medium font-bold">
                                            {(() => {
                                                const groupVal = event.linkedGroup?.slug || event.linkedGroup?._id || event.linkedGroup;
                                                if (!groupVal) return 'Keine';
                                                const grp = groups.find(g => g._id === groupVal || g.slug === groupVal);
                                                return grp ? `${grp.name} (${grp.altersstufe || 'Gruppe'})` : (event.linkedGroup?.name || 'Verknüpft');
                                            })()}
                                        </span>
                                    </div>
                                </EditableField>
                            </div>
                            <div className="space-y-2">
                                <label className="text-label-small font-bold text-surface-onVariant">Status</label>
                                <EditableField value={event.status || 'published'} onSave={v => saveField('status', v)} type="select" label="Status"
                                    selectOptions={[{value:'draft',label:'Entwurf'},{value:'published',label:'Veröffentlicht'},{value:'archived',label:'Archiviert'}]}>
                                    <div className="p-3 rounded-lg bg-surface-container">
                                        <span className={`text-body-medium font-bold ${event.status === 'published' ? 'text-primary' : event.status === 'draft' ? 'text-surface-onVariant' : 'text-error'}`}>
                                            {event.status === 'published' ? 'Veröffentlicht' : event.status === 'draft' ? 'Entwurf' : 'Archiviert'}
                                        </span>
                                    </div>
                                </EditableField>
                            </div>
                            <div className="space-y-2">
                                <label className="text-label-small font-bold text-surface-onVariant">URL-Slug</label>
                                <EditableField value={event.slug || ''} onSave={v => saveField('slug', v)} label="URL-Slug">
                                    <div className="p-3 rounded-lg bg-surface-container font-mono text-body-small">{event.slug || '—'}</div>
                                </EditableField>
                            </div>
                            <div className="space-y-2">
                                <label className="text-label-small font-bold text-surface-onVariant">Max. Teilnehmer</label>
                                <EditableField value={event.maxParticipants || ''} onSave={v => saveField('maxParticipants', v)} type="number" label="Max. Teilnehmer" placeholder="Unbegrenzt">
                                    <div className="p-3 rounded-lg bg-surface-container"><span className="text-body-medium">{event.maxParticipants || 'Unbegrenzt'}</span></div>
                                </EditableField>
                            </div>
                            <div className="space-y-2">
                                <label className="text-label-small font-bold text-surface-onVariant">Anmeldeschluss</label>
                                <EditableField value={event.registrationDeadline ? new Date(event.registrationDeadline).toISOString().split('T')[0] : ''} onSave={v => saveField('registrationDeadline', v)} type="date" label="Anmeldeschluss">
                                    <div className="p-3 rounded-lg bg-surface-container"><span className="text-body-medium">{event.registrationDeadline ? new Date(event.registrationDeadline).toLocaleDateString('de-DE') : 'Kein Limit'}</span></div>
                                </EditableField>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            <EditableField value={event.isRegistrationEnabled || false} onSave={v => saveField('isRegistrationEnabled', v)} type="toggle">
                                <span className="text-body-medium font-bold">Anmeldung aktiviert</span>
                            </EditableField>
                            {event.isRegistrationEnabled && (
                                <EditableField value={event.requiresApproval || false} onSave={v => saveField('requiresApproval', v)} type="toggle">
                                    <span className="text-body-medium">Manuelle Freigabe</span>
                                </EditableField>
                            )}
                            <EditableField value={event.requiresConsent || false} onSave={v => saveField('requiresConsent', v)} type="toggle">
                                <span className="text-body-medium">Einverständniserklärung</span>
                            </EditableField>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Panel */}
            {activePanel === 'email' && (
                <div className="border-b border-outlineVariant bg-surface-containerLow animate-fade-in">
                    <div className="container mx-auto px-4 py-6 max-w-4xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-title-medium font-bold flex items-center gap-2">
                                <Mail sx={{ fontSize: 20 }} className="text-primary" /> Bestätigungs-E-Mail
                            </h3>
                            <button onClick={() => setActivePanel(null)} className="md3-icon-btn"><X sx={{ fontSize: 18 }} /></button>
                        </div>
                        <div className="space-y-4">
                            <EditableField value={event.registrationEmailTemplate?.subject || `Anmeldung bestätigt: ${event.title}`}
                                onSave={v => saveField('registrationEmailTemplate', { ...event.registrationEmailTemplate, subject: v })} label="Betreff">
                                <div className="p-3 rounded-lg bg-surface-container">
                                    <p className="text-label-small text-surface-onVariant">Betreff</p>
                                    <p className="text-body-medium font-bold">{event.registrationEmailTemplate?.subject || `Anmeldung bestätigt: ${event.title}`}</p>
                                </div>
                            </EditableField>
                            <EditableField value={event.registrationEmailTemplate?.bodyTemplate || ''} type="textarea" rows={8}
                                onSave={v => saveField('registrationEmailTemplate', { ...event.registrationEmailTemplate, bodyTemplate: v })} label="Nachricht">
                                <div className="p-3 rounded-lg bg-surface-container">
                                    <p className="text-label-small text-surface-onVariant">Nachricht</p>
                                    <p className="text-body-small whitespace-pre-wrap line-clamp-4">{event.registrationEmailTemplate?.bodyTemplate || 'Klicke um eine E-Mail-Vorlage zu erstellen...'}</p>
                                </div>
                            </EditableField>
                            <p className="text-body-small text-surface-onVariant italic">Platzhalter: {"{{name}}"}, {"{{eventTitle}}"}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 pt-8 max-w-5xl relative z-10">
                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-1 min-w-0">
                        {/* Title Card */}
                        <div className="md3-card-elevated bg-surface-containerLow p-6 md:p-10 mb-8 shadow-elevation-2 rounded-[2.5rem] border border-outlineVariant/30">
                            <div className="mb-6">
                                <EditableField value={pageContent?.hero?.title || event.title} onSave={v => saveField('pageContent.hero.title', v)} label="Titel">
                                    <h1 className="text-display-small md:text-display-medium font-bold text-surface-on mb-4 leading-tight">
                                        {pageContent?.hero?.title || event.title}
                                    </h1>
                                </EditableField>
                                <EditableField value={pageContent?.hero?.subtitle || ''} onSave={v => saveField('pageContent.hero.subtitle', v)} label="Untertitel">
                                    <p className="text-headline-small text-surface-onVariant font-normal">
                                        {pageContent?.hero?.subtitle || 'Klicke um einen Untertitel hinzuzufügen'}
                                    </p>
                                </EditableField>
                            </div>

                            {/* Framed & Embedded Hero Image */}
                            <div className="mb-8">
                                <label className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider block mb-2">
                                    Event-Bild (Umrahmt & Eingebettet)
                                </label>
                                <EditableField value={pageContent?.hero?.imageUrl || ''} onSave={v => saveField('pageContent.hero.imageUrl', v)} type="url" label="Event-Bild URL" onUpload={handleImageUpload}>
                                    {pageContent?.hero?.imageUrl ? (
                                        <div className="p-2 md:p-3 rounded-[2rem] bg-surface-containerHigh/60 border border-outlineVariant/50 shadow-elevation-2">
                                            <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] max-h-[460px] rounded-[1.4rem] overflow-hidden bg-surface-containerLow flex items-center justify-center">
                                                <img src={pageContent.hero.imageUrl} alt="" className="w-full h-full object-contain sm:object-cover md:object-contain rounded-[1.4rem]" />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-[2rem] border-2 border-dashed border-outlineVariant/60 p-8 text-center text-surface-onVariant bg-surface-container/40 hover:bg-surface-container/70 transition-colors cursor-pointer">
                                            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-2">
                                                <Calendar size={24} />
                                            </div>
                                            <p className="text-title-small font-bold">Klicke um ein Event-Bild hinzuzufügen</p>
                                            <p className="text-body-small opacity-70">Wird umrahmt und harmonisch im Beitrag eingebettet</p>
                                        </div>
                                    )}
                                </EditableField>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-outlineVariant">
                                <EditableField value={event.date ? new Date(event.date).toISOString().split('T')[0] : ''} onSave={v => saveField('date', v)} type="date" label="Datum">
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-surface-container">
                                        <div className="w-12 h-12 rounded-full bg-secondary-container text-secondary-onContainer flex items-center justify-center">
                                            <Calendar size={24} />
                                        </div>
                                        <div>
                                            <p className="text-label-medium text-surface-onVariant">Datum</p>
                                            <p className="text-title-medium text-surface-on font-bold">
                                                {event.date ? new Date(event.date).toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                                            </p>
                                        </div>
                                    </div>
                                </EditableField>

                                <EditableField value={event.startTime || ''} onSave={v => saveField('startTime', v)} type="time" label="Startzeit">
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-surface-container">
                                        <div className="w-12 h-12 rounded-full bg-tertiary-container text-tertiary-onContainer flex items-center justify-center">
                                            <Clock size={24} />
                                        </div>
                                        <div>
                                            <p className="text-label-medium text-surface-onVariant">Startzeit</p>
                                            <p className="text-title-medium text-surface-on font-bold">
                                                {event.startTime || '—'} Uhr
                                            </p>
                                        </div>
                                    </div>
                                </EditableField>

                                <EditableField value={event.endTime || ''} onSave={v => saveField('endTime', v)} type="time" label="Endzeit">
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-surface-container">
                                        <div className="w-12 h-12 rounded-full bg-tertiary-container/60 text-tertiary-onContainer flex items-center justify-center">
                                            <Clock size={24} />
                                        </div>
                                        <div>
                                            <p className="text-label-medium text-surface-onVariant">Endzeit</p>
                                            <p className="text-title-medium text-surface-on font-bold">
                                                {event.endTime || '—'} Uhr
                                            </p>
                                        </div>
                                    </div>
                                </EditableField>

                                <EditableField value={event.location || ''} onSave={v => saveField('location', v)} label="Ort">
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-surface-container">
                                        <div className="w-12 h-12 rounded-full bg-primary-container text-primary-onContainer flex items-center justify-center">
                                            <MapPin size={24} />
                                        </div>
                                        <div>
                                            <p className="text-label-medium text-surface-onVariant">Ort</p>
                                            <p className="text-title-medium text-surface-on font-bold">{event.location || 'Ort hinzufügen'}</p>
                                        </div>
                                    </div>
                                </EditableField>

                                {(event.link || true) && (
                                    <EditableField value={event.link || ''} onSave={v => saveField('link', v)} type="url" label="Externer Link" className="md:col-span-2">
                                        <div className="flex items-center gap-4 p-4 rounded-xl bg-surface-container">
                                            <div className="w-12 h-12 rounded-full bg-surface-containerHighest text-surface-onVariant flex items-center justify-center">
                                                <Info size={24} />
                                            </div>
                                            <div>
                                                <p className="text-label-medium text-surface-onVariant">Externer Link</p>
                                                <p className="text-title-medium text-surface-on font-bold">{event.link || 'Link hinzufügen'}</p>
                                            </div>
                                        </div>
                                    </EditableField>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        {(!pageContent?.sections || pageContent.sections.length === 0) && (
                            <div className="md3-card-elevated bg-surface-containerLow p-6 md:p-8 mb-8">
                                <EditableField value={event.description || ''} onSave={v => saveField('description', v)} type="textarea" label="Beschreibung" rows={6}>
                                    <div>
                                        <h3 className="text-headline-small font-bold mb-4 text-surface-on">Beschreibung</h3>
                                        <p className="whitespace-pre-wrap text-body-large text-surface-onVariant">{event.description || 'Beschreibung hinzufügen...'}</p>
                                    </div>
                                </EditableField>
                            </div>
                        )}

                        {/* Dynamic Sections — Edit + Preview combined */}
                        <div className="mb-8">
                            <h3 className="text-title-medium font-bold text-primary mb-4 flex items-center gap-2">
                                <Edit2 sx={{ fontSize: 18 }} /> Sektionen
                            </h3>

                            {pageContent?.sections?.length > 0 ? (
                                <div className="space-y-3">
                                    {pageContent.sections.map((section, idx) => (
                                        <SectionEditor key={idx} section={section} index={idx}
                                            onUpdate={handleSectionUpdate} onDelete={handleSectionDelete}
                                            onMove={handleSectionMove} totalSections={pageContent.sections.length}
                                            iconMap={iconMap} onImageUpload={handleImageUpload} />
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center border-2 border-dashed border-outlineVariant rounded-2xl text-surface-onVariant">
                                    Noch keine Sektionen. Füge eine hinzu, um die Event-Seite zu gestalten.
                                </div>
                            )}

                            {/* Add section — type-specific buttons */}
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button onClick={() => handleAddSection('text')} className="md3-btn-tonal gap-1.5 text-label-small">
                                    <Plus sx={{ fontSize: 16 }} /> Text
                                </button>
                                <button onClick={() => handleAddSection('icon-list')} className="md3-btn-tonal gap-1.5 text-label-small">
                                    <Plus sx={{ fontSize: 16 }} /> Icon-Liste
                                </button>
                                <button onClick={() => handleAddSection('list')} className="md3-btn-tonal gap-1.5 text-label-small">
                                    <Plus sx={{ fontSize: 16 }} /> Liste
                                </button>
                                <button onClick={() => handleAddSection('grid')} className="md3-btn-tonal gap-1.5 text-label-small">
                                    <Plus sx={{ fontSize: 16 }} /> Grid
                                </button>
                                <button onClick={() => handleAddSection('info-box')} className="md3-btn-tonal gap-1.5 text-label-small">
                                    <Plus sx={{ fontSize: 16 }} /> Info-Box
                                </button>
                                <button onClick={() => handleAddSection('image')} className="md3-btn-tonal gap-1.5 text-label-small">
                                    <Plus sx={{ fontSize: 16 }} /> Bild
                                </button>
                            </div>
                        </div>

                        {/* Live Preview of all sections */}
                        {pageContent?.sections?.length > 0 && (
                            <div>
                                <h4 className="text-label-small text-surface-onVariant uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <span>Vorschau</span>
                                    <span className="text-[10px] bg-surface-container px-2 py-0.5 rounded-full">so sieht es auf der Seite aus</span>
                                </h4>
                                {pageContent.sections.map((s, i) => renderSection(s, i))}
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:w-80 flex-shrink-0">
                        <div className="sticky top-20 space-y-6">
                            <div className="md3-card-outlined p-6">
                                <h3 className="text-title-medium font-bold mb-4">Quick-Einstellungen</h3>
                                <EditableField value={event.title || ''} onSave={v => saveField('title', v)} label="Event-Titel (intern)">
                                    <div className="p-3 rounded-lg bg-surface-container">
                                        <p className="text-label-small text-surface-onVariant">Interner Titel</p>
                                        <p className="text-body-medium font-bold">{event.title}</p>
                                    </div>
                                </EditableField>
                                <div className="mt-3">
                                    <EditableField value={event.description || ''} onSave={v => saveField('description', v)} type="textarea" label="Beschreibung" rows={3}>
                                        <div className="p-3 rounded-lg bg-surface-container">
                                            <p className="text-label-small text-surface-onVariant">Beschreibung</p>
                                            <p className="text-body-small line-clamp-3">{event.description || '—'}</p>
                                        </div>
                                    </EditableField>
                                </div>
                            </div>

                            {/* Event Card Preview */}
                            <div>
                                <h4 className="text-label-small text-surface-onVariant uppercase tracking-wider mb-3 flex items-center gap-2">
                                    Event-Karte
                                    <span className="text-[10px] bg-surface-container px-2 py-0.5 rounded-full">so sieht sie aus</span>
                                </h4>
                                <div className="pointer-events-none">
                                    <EventCard event={event} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditableEventPage;
